import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UsePaginationParamsOptions {
  /** 1-based default page when not set in URL. Default: 1 */
  defaultPage?: number;
  /** Default items per page when not set in URL. Default: 10 */
  defaultPageSize?: number;
  /** URL search parameter key to use for page size. Default: 'pageSize' */
  pageSizeParamKey?: 'pageSize' | 'perPage' | 'limit';
}

export interface UsePaginationParamsReturn {
  page: number;
  pageSize: number;
  /** Alias for pageSize (compatible with hooks expecting perPage) */
  perPage: number;
  /** Alias for pageSize (compatible with hooks expecting limit) */
  limit: number;
  setPage: (newPageOrUpdater: number | ((prev: number) => number)) => void;
  setPageSize: (newSize: number) => void;
  resetPage: () => void;
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

/**
 * Hook to bind table/list pagination directly to URL search parameters.
 * Supports deep-linking, refresh-safe pagination, and browser navigation.
 */
export function usePaginationParams(options?: UsePaginationParamsOptions): UsePaginationParamsReturn {
  const defaultPage = options?.defaultPage ?? 1;
  const defaultPageSize = options?.defaultPageSize ?? 10;
  const key = options?.pageSizeParamKey ?? 'pageSize';

  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => {
    const raw = Number(searchParams.get('page'));
    return Number.isSafeInteger(raw) && raw > 0 ? raw : defaultPage;
  }, [searchParams, defaultPage]);

  const pageSize = useMemo(() => {
    const raw = Number(
      searchParams.get(key) ??
        searchParams.get('pageSize') ??
        searchParams.get('perPage') ??
        searchParams.get('limit')
    );
    return Number.isSafeInteger(raw) && raw > 0 ? raw : defaultPageSize;
  }, [searchParams, key, defaultPageSize]);

  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const defaultPageRef = useRef(defaultPage);
  defaultPageRef.current = defaultPage;

  const defaultPageSizeRef = useRef(defaultPageSize);
  defaultPageSizeRef.current = defaultPageSize;

  const keyRef = useRef(key);
  keyRef.current = key;

  const setPage = useCallback(
    (newPageOrUpdater: number | ((prev: number) => number)) => {
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          const prevVal = Number(next.get('page')) || defaultPageRef.current;
          const nextVal =
            typeof newPageOrUpdater === 'function' ? newPageOrUpdater(prevVal) : newPageOrUpdater;
          const validVal = Number.isSafeInteger(nextVal) && nextVal > 0 ? nextVal : 1;
          next.set('page', String(validVal));
          return next;
        },
        { replace: true }
      );
    },
    []
  );

  const setPageSize = useCallback(
    (newSize: number) => {
      const activeKey = keyRef.current;
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          const validSize =
            Number.isSafeInteger(newSize) && newSize > 0 ? newSize : defaultPageSizeRef.current;
          next.set(activeKey, String(validSize));
          // Remove alternate keys so there's no mismatch
          if (activeKey !== 'pageSize') next.delete('pageSize');
          if (activeKey !== 'perPage') next.delete('perPage');
          if (activeKey !== 'limit') next.delete('limit');
          // Reset page to 1 when changing page size
          next.set('page', '1');
          return next;
        },
        { replace: true }
      );
    },
    []
  );

  const resetPage = useCallback(() => {
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', '1');
        return next;
      },
      { replace: true }
    );
  }, []);

  return {
    page,
    pageSize,
    perPage: pageSize,
    limit: pageSize,
    setPage,
    setPageSize,
    resetPage,
    searchParams,
    setSearchParams,
  };
}

export default usePaginationParams;
